import { ArrowLeft, Heart, MessageCircle, Sparkles, Star, Gift } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function GratitudeExpressionGuide() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-amber-700 hover:text-amber-800 hover:bg-amber-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Week
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-amber-100 rounded-full">
              <Heart className="w-8 h-8 text-amber-600" />
            </div>
            <div>
              <h1 className="text-3xl font-serif font-bold text-gray-900">Gratitude Expression</h1>
              <p className="text-amber-600">Week 10, Day 4 - Social Connections</p>
            </div>
          </div>

          <div className="prose prose-amber max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Today, move beyond a simple "thanks" and tell someone specifically what you appreciate about 
              them. This practice strengthens relationships and creates positive feelings for both of you.
            </p>

            <div className="bg-amber-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-amber-800 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Why Specific Gratitude Matters
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Generic thanks often goes unnoticed</li>
                <li>• Specific appreciation shows you truly see them</li>
                <li>• It reinforces positive behaviors in relationships</li>
                <li>• Both giver and receiver experience mood boosts</li>
                <li>• Creates deeper connection and trust</li>
              </ul>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">The Difference</h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gray-100 rounded-xl p-5 border-2 border-gray-300">
                <h3 className="font-semibold text-gray-500 mb-3">Generic Thanks</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>"Thanks for everything"</li>
                  <li>"You're the best"</li>
                  <li>"Thanks for being there"</li>
                  <li>"I appreciate you"</li>
                </ul>
              </div>
              
              <div className="bg-amber-50 rounded-xl p-5 border-2 border-amber-400">
                <h3 className="font-semibold text-amber-700 mb-3">Specific Appreciation</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>"I appreciate how you always listen without judgment when I'm stressed"</li>
                  <li>"Thank you for remembering my presentation was today and texting good luck"</li>
                  <li>"I noticed you made my favorite coffee this morning - that was so thoughtful"</li>
                </ul>
              </div>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Formula for Specific Gratitude</h2>
            
            <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-xl p-6 mb-8">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold shrink-0">1</div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Name the behavior</h3>
                    <p className="text-gray-600">What specifically did they do?</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold shrink-0">2</div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Share the impact</h3>
                    <p className="text-gray-600">How did it make you feel or help you?</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold shrink-0">3</div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Acknowledge the quality</h3>
                    <p className="text-gray-600">What does it say about who they are?</p>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Examples to Inspire You</h2>
            
            <div className="space-y-4 mb-8">
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-amber-400">
                <p className="text-gray-700 italic">"I appreciate how you checked in on me last week when I seemed off. It made me feel seen and cared for. Your attentiveness is such a gift."</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-amber-400">
                <p className="text-gray-700 italic">"Thank you for always being honest with me, even when it's hard. Knowing I can trust your feedback helps me grow. Your integrity means so much to me."</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-amber-400">
                <p className="text-gray-700 italic">"I noticed how you handled that difficult situation with such patience. It reminded me why I admire you so much."</p>
              </div>
            </div>

            <div className="bg-rose-50 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-rose-800 mb-4 flex items-center gap-2">
                <Gift className="w-5 h-5" />
                Ways to Express Gratitude
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Tell them in person (most powerful)</li>
                <li>• Send a thoughtful text or voice message</li>
                <li>• Write a handwritten note</li>
                <li>• Say it during a phone call</li>
                <li>• Share it publicly (if appropriate)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
